import { createFileRoute } from "@tanstack/react-router";
import ArticleSouthFloridaPoolRemodelingGuide from "@/pages/ArticleSouthFloridaPoolRemodelingGuide";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE } from "@/data/blogData";
import { FAQS_SOUTH_FLORIDA_POOL_REMODELING_GUIDE } from "@/data/southFloridaPoolRemodelingGuideData";

const PATH = "/blog/south-florida-pool-remodeling-guide";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline:
    "South Florida Pool Remodeling Guide: Resurfacing, Tile, Coping, Decks, Costs, and Planning",
  description:
    "Plan a South Florida pool remodel by understanding resurfacing, tile, coping, pool decks, costs, sequencing, and how to define the right project scope.",
  datePublished: "2026-10-05",
  dateModified: "2026-10-05",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};
if (FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE) {
  blogPosting.image = FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "South Florida Pool Remodeling Guide",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content.
if (FAQS_SOUTH_FLORIDA_POOL_REMODELING_GUIDE.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_SOUTH_FLORIDA_POOL_REMODELING_GUIDE.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/south-florida-pool-remodeling-guide",
)({
  head: () =>
    buildHead({
      title: "South Florida Pool Remodeling Guide | Precision",
      description:
        "Plan a South Florida pool remodel by understanding resurfacing, tile, coping, pool decks, costs, sequencing, and how to define the right project scope.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_SOUTH_FLORIDA_GUIDE || undefined,
      jsonLd,
    }),
  component: ArticleSouthFloridaPoolRemodelingGuide,
});
